import { useMutation } from '@tanstack/react-query';
import { updateQuoteV2ItemQuantityMutation } from 'Services/Mutations/QuotesV2';
import type { QuoteV2ItemWithTotalsResponse } from 'Services/Query/QuotesV2/Types';
import {
  updateItemInTree,
  updateItemFromServerResponse
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface UpdateQuantityVariables {
  itemId: string;
  value: number;
}

/**
 * Update item quantity via the dedicated v2 endpoint.
 * Applies optimistic update immediately, then syncs with server response.
 */
export const useUpdateItemQuantity = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = updateQuoteV2ItemQuantityMutation();

  return useMutation({
    mutationFn: ({ itemId, value }: UpdateQuantityVariables) =>
      mutationFn({ quoteId, itemId, value }),

    onMutate: async ({ itemId, value }) => {
      await cache.cancelItems();
      const previousItems = cache.getItems();

      cache.setItems(old =>
        updateItemInTree(old || [], itemId, { quantity: value })
      );

      return { previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: data => {
      const response = data as QuoteV2ItemWithTotalsResponse;

      if (response?.item) {
        cache.setItems(old =>
          updateItemFromServerResponse(old || [], response.item, p => ({
            quantity: p.quantity,
            sell: p.sell,
            cost: p.cost,
            totalGrossProfit: p.marginAmount,
            totalGrossProfitPercentage: p.margin
          }))
        );
      }

      if (response?.totals) {
        cache.setQuote(old =>
          old
            ? {
                ...old,
                totalSell: response.totals.totalSell,
                totalCost: response.totals.totalCost
              }
            : old
        );
      }

      cache.invalidate();
    }
  });
};
